import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Input } from "@v2/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { EmptyState, SectionLoading } from "../../components/state";
import { calculateSquadScore } from "../../lib/squad-score";
import { useWorkspaceData } from "../../lib/workspace-data";
import { matchesPlayedByPlayer } from "./stats-utils";
import { supabaseV2 } from "../../lib/supabase";
import type { SquadScoreSetting } from "../../types";

type RangePreset = "all" | "30d" | "90d" | "month" | "month_prev" | "custom";

const PRESET_KEYS: RangePreset[] = ["all", "30d", "90d", "month", "month_prev", "custom"];

function presetBounds(
  preset: RangePreset,
  customFrom: string,
  customTo: string,
  nowMs?: number,
): { from: number | null; to: number | null } {
  // nowMs: fine stagione quando si guarda una stagione chiusa.
  const now = new Date(nowMs ?? Date.now());
  if (preset === "all") return { from: null, to: null };
  if (preset === "custom") {
    return {
      from: customFrom ? new Date(customFrom).getTime() : null,
      to: customTo ? new Date(customTo + "T23:59:59").getTime() : null,
    };
  }
  if (preset === "30d") {
    const from = new Date(now);
    from.setDate(now.getDate() - 30);
    return { from: from.getTime(), to: now.getTime() };
  }
  if (preset === "90d") {
    const from = new Date(now);
    from.setDate(now.getDate() - 90);
    return { from: from.getTime(), to: now.getTime() };
  }
  if (preset === "month") {
    return { from: new Date(now.getFullYear(), now.getMonth(), 1).getTime(), to: now.getTime() };
  }
  // month_prev
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  return { from: from.getTime(), to: to.getTime() };
}

type SortKey =
  | "name" | "role" | "matches" | "starts" | "goals" | "assists" | "ga"
  | "goalsPerMatch" | "assistsPerMatch" | "yellow" | "red"
  | "present" | "late" | "absent" | "noResponse" | "presenceRate" | "score";

type SortDir = "asc" | "desc";

type Row = {
  playerId: string;
  name: string;
  role: string;
  matches: number;
  starts: number;
  goals: number;
  assists: number;
  ga: number;
  goalsPerMatch: number;
  assistsPerMatch: number;
  yellow: number;
  red: number;
  present: number;
  late: number;
  absent: number;
  noResponse: number;
  presenceTotal: number;
  presenceRate: number;
  score: number;
};

const COLUMNS: Array<{ key: SortKey; labelKey: string; tooltipKey?: string; align: "left" | "right"; defaultDir: SortDir }> = [
  { key: "name", labelKey: "fullTable.col.name", align: "left", defaultDir: "asc" },
  { key: "role", labelKey: "fullTable.col.role", align: "left", defaultDir: "asc" },
  { key: "matches", labelKey: "fullTable.col.matches", tooltipKey: "fullTable.col.matchesTip", align: "right", defaultDir: "desc" },
  { key: "starts", labelKey: "fullTable.col.starts", tooltipKey: "fullTable.col.startsTip", align: "right", defaultDir: "desc" },
  { key: "goals", labelKey: "fullTable.col.g", tooltipKey: "fullTable.col.gTip", align: "right", defaultDir: "desc" },
  { key: "assists", labelKey: "fullTable.col.a", tooltipKey: "fullTable.col.aTip", align: "right", defaultDir: "desc" },
  { key: "ga", labelKey: "fullTable.col.ga", align: "right", defaultDir: "desc" },
  { key: "goalsPerMatch", labelKey: "fullTable.col.gPerMatch", tooltipKey: "fullTable.col.gPerMatchTip", align: "right", defaultDir: "desc" },
  { key: "assistsPerMatch", labelKey: "fullTable.col.aPerMatch", tooltipKey: "fullTable.col.aPerMatchTip", align: "right", defaultDir: "desc" },
  { key: "yellow", labelKey: "fullTable.col.yellow", tooltipKey: "fullTable.col.yellowTip", align: "right", defaultDir: "desc" },
  { key: "red", labelKey: "fullTable.col.red", tooltipKey: "fullTable.col.redTip", align: "right", defaultDir: "desc" },
  { key: "present", labelKey: "fullTable.col.present", tooltipKey: "fullTable.col.presentTip", align: "right", defaultDir: "desc" },
  { key: "late", labelKey: "fullTable.col.late", tooltipKey: "fullTable.col.lateTip", align: "right", defaultDir: "desc" },
  { key: "absent", labelKey: "fullTable.col.absent", tooltipKey: "fullTable.col.absentTip", align: "right", defaultDir: "desc" },
  { key: "noResponse", labelKey: "fullTable.col.nr", tooltipKey: "fullTable.col.nrTip", align: "right", defaultDir: "desc" },
  { key: "presenceRate", labelKey: "fullTable.col.presPct", tooltipKey: "fullTable.col.presPctTip", align: "right", defaultDir: "desc" },
  { key: "score", labelKey: "fullTable.col.score", align: "right", defaultDir: "desc" },
];

export function PlayersFullTable({
  teamId,
  allSeasons = false,
  referenceNow,
  competitionId = null,
}: {
  teamId: string;
  allSeasons?: boolean;
  referenceNow?: number;
  competitionId?: string | null;
}) {
  const { t } = useTranslation("stats");
  const { players, attendance: responses, matches: allMatches, matchStats, isLoading } = useWorkspaceData(teamId, { allSeasons, competitionId });
  const settingQuery = useQuery({
    queryKey: ["squad-score-settings", teamId, "active"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as SquadScoreSetting | null;
    },
    staleTime: 60_000,
  });
  const setting = settingQuery.data;

  const [preset, setPreset] = useState<RangePreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [search, setSearch] = useState("");
  const [includeGuests, setIncludeGuests] = useState(false);

  const { from, to } = presetBounds(preset, customFrom, customTo, referenceNow);

  const rows: Row[] = useMemo(() => {
    const inRange = (timestamp?: string | null): boolean => {
      if (from === null && to === null) return true;
      if (!timestamp) return false;
      const ts = new Date(timestamp).getTime();
      if (from !== null && ts < from) return false;
      if (to !== null && ts > to) return false;
      return true;
    };
    const filteredResponses = responses.filter((r) => inRange(r.session_starts_at));
    const filteredStats = matchStats.filter((s) => inRange(s.match_starts_at));
    const filteredMatches = allMatches.filter((m) => inRange(m.starts_at));
    const played = matchesPlayedByPlayer(filteredMatches, filteredResponses, filteredStats);

    return players
      .filter((p) => includeGuests || !p.is_guest)
      .map((player): Row => {
        const playerResponses = filteredResponses.filter((r) => r.player_id === player.id);
        const playerStats = filteredStats.filter((s) => s.player_id === player.id);
        const present = playerResponses.filter((r) => r.status === "present").length;
        const late = playerResponses.filter((r) => r.status === "late").length;
        const absent = playerResponses.filter((r) => r.status === "absent").length;
        const noResponse = playerResponses.filter((r) => r.status === "no_response").length;
        const presenceTotal = present + late + absent;
        const presenceRate = presenceTotal > 0 ? Math.round(((present + late) / presenceTotal) * 100) : 0;
        const goals = playerStats.reduce((acc, s) => acc + (s.goals || 0), 0);
        const assists = playerStats.reduce((acc, s) => acc + (s.assists || 0), 0);
        const matches = played.get(player.id) || 0;
        const starts = playerStats.filter((s) => s.started).length;
        const yellow = playerStats.reduce((acc, s) => acc + (s.yellow_cards || 0), 0);
        const red = playerStats.reduce((acc, s) => acc + (s.red_cards || 0), 0);
        const score = setting ? calculateSquadScore(playerResponses, playerStats, setting, referenceNow) : 0;
        return {
          playerId: player.id,
          name: player.display_name,
          role: player.role_code || "-",
          matches,
          starts,
          goals,
          assists,
          ga: goals + assists,
          goalsPerMatch: matches > 0 ? goals / matches : 0,
          assistsPerMatch: matches > 0 ? assists / matches : 0,
          yellow,
          red,
          present,
          late,
          absent,
          noResponse,
          presenceTotal,
          presenceRate,
          score,
        };
      });
  }, [players, responses, allMatches, matchStats, setting, from, to, includeGuests, referenceNow]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = needle
      ? rows.filter((r) => `${r.name} ${r.role}`.toLowerCase().includes(needle))
      : rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "string" && typeof vb === "string") return va.localeCompare(vb) * dir;
      const na = Number(va);
      const nb = Number(vb);
      if (na === nb) return a.name.localeCompare(b.name);
      return (na - nb) * dir;
    });
  }, [rows, search, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      const col = COLUMNS.find((c) => c.key === key);
      setSortDir(col?.defaultDir || "desc");
    }
  };

  if (isLoading) return <SectionLoading />;

  const presetLabel = (key: RangePreset): string => {
    if (key === "all") return t("range.all");
    if (key === "30d") return t("range.30d");
    if (key === "90d") return t("range.90d");
    if (key === "month") return t("range.month");
    if (key === "month_prev") return t("range.month_prev");
    return t("range.custom");
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between flex-wrap">
          <div>
            <CardTitle className="text-lg">{t("fullTable.title")}</CardTitle>
            <CardDescription>
              {t("fullTable.description")}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="relative w-full sm:w-52">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("fullTable.search")}
                className="pl-9"
              />
            </div>
            <Select value={preset} onValueChange={(value) => setPreset(value as RangePreset)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESET_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {presetLabel(key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => setIncludeGuests((v) => !v)}
              className={cn(
                "h-11 px-4 text-sm rounded-full transition-colors",
                includeGuests ? "bg-primary text-primary-foreground" : "bg-raised text-muted-foreground hover:text-foreground",
              )}
              title={t("fullTable.guestsToggleTitle")}
            >
              {includeGuests ? t("fullTable.guestsIncluded") : t("fullTable.rosterOnly")}
            </button>
          </div>
        </div>
        {preset === "custom" && (
          <div className="flex flex-wrap gap-2 mt-2">
            <label className="text-xs text-muted-foreground flex items-center gap-2">
              {t("fullTable.from")}
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-8 w-40"
              />
            </label>
            <label className="text-xs text-muted-foreground flex items-center gap-2">
              {t("fullTable.to")}
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-8 w-40"
              />
            </label>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {filteredRows.length === 0 ? (
          <EmptyState text={t("fullTable.empty")} />
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b">
                  {COLUMNS.map((col) => {
                    const active = sortKey === col.key;
                    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                    return (
                      <th
                        key={col.key}
                        scope="col"
                        title={col.tooltipKey ? (t(col.tooltipKey) as string) : undefined}
                        className={cn(
                          "px-2 py-2 eb-data text-[13px] whitespace-nowrap select-none cursor-pointer hover:bg-muted/40",
                          col.align === "right" ? "text-right" : "text-left",
                          active && "text-foreground",
                          !active && "text-muted-foreground",
                        )}
                        onClick={() => handleSort(col.key)}
                      >
                        <span className={cn("inline-flex items-center gap-1", col.align === "right" && "justify-end")}>
                          {t(col.labelKey)}
                          <Icon className="h-3 w-3 opacity-60" />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.playerId} className="border-b last:border-b-0 hover:bg-muted/30">
                    <td className="px-2 py-2 font-medium whitespace-nowrap">{r.name}</td>
                    <td className="px-2 py-2 text-muted-foreground">{r.role}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.matches}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.starts}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold">{r.goals}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.assists}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold">{r.ga}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{r.goalsPerMatch.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{r.assistsPerMatch.toFixed(2)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-amber-700">{r.yellow || ""}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-rose-600">{r.red || ""}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.present}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.late}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.absent}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{r.noResponse}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.presenceRate}%</td>
                    <td className="px-2 py-2 text-right tabular-nums font-bold text-base">{r.score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
